import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';

import { Breadcrumb, GoogleFileDto } from "../../dto";
import { HttpFilesService } from "../../services/files/http-files.service";

@Component({
    selector: 'app-drive-file-picker-modal',
    standalone: true,
    imports: [
        CommonModule,
        NzModalModule,
        NzTableModule,
        NzButtonModule,
        NzIconModule,
        NzBreadCrumbModule
    ],
    templateUrl: './drive-file-picker-modal.component.html',
    styleUrls: ['./drive-file-picker-modal.component.scss']
})
export class DriveFilePickerModalComponent {
    @Output() fileSelected = new EventEmitter<{ file: GoogleFileDto, folderId?: string }>();

    isVisible = false;
    files: GoogleFileDto[] = [];
    loading = false; 
    
    currentFolderId?: string;
    breadcrumbs: Breadcrumb[] = [{ id: this.currentFolderId, name: 'Home' }];

    fileType?: string;

    constructor(
        private toastr: ToastrService, 
        private httpService: HttpFilesService
    ) { }

    open(startingFolderId?: string, fileType?: string) {
        this.isVisible = true;
        this.fileType = fileType;
        this.currentFolderId = startingFolderId;

        if (startingFolderId) {
            // Let the backend do the heavy lifting!
            this.loading = true;
            this.httpService.getBreadcrumbs(startingFolderId).subscribe({
                next: (trail) => {
                    this.breadcrumbs = trail;
                    this.fetchFilesForFolder(); // Fetch the files for the folder now that we have the path
                },
                error: (err) => {
                    console.error('Failed to load breadcrumbs', err);
                    // Fallback if the API fails
                    this.breadcrumbs = [{ id: undefined, name: 'Home' }, { id: startingFolderId, name: 'Folder' }];
                    this.fetchFilesForFolder();
                }
            });
        } else {
            // Start at root
            this.breadcrumbs = [{ id: undefined, name: 'Home' }];
            this.fetchFilesForFolder();
        }
    }

    handleCancel() {
        this.isVisible = false;
    }

    fetchFilesForFolder() {
        this.loading = true;
        this.httpService.getAllFiles(this.currentFolderId) 
            .pipe(
                catchError(error => {
                    console.error('Error fetching files', error);
                    this.toastr.error('Failed to fetch files');
                    throw error;
                }),
                finalize(() => this.loading = false)
            )
            .subscribe(data => {
                this.files = data;
            });
    }

    openFolder(folder: GoogleFileDto) {
        if (this.isFolder(folder.mimeType)) {
            this.currentFolderId = folder.id;
            this.breadcrumbs.push({ id: folder.id, name: folder.name });
            this.fetchFilesForFolder();
        }
    }

    navigateToBreadcrumb(index: number, breadcrumb: Breadcrumb) {
        this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
        this.currentFolderId = breadcrumb.id;
        this.fetchFilesForFolder();
    }

    isFolder(mimeType: string): boolean {
        return mimeType === 'application/vnd.google-apps.folder';
    }

    selectFile(file: GoogleFileDto) {
        this.fileSelected.emit({
            file: file,
            folderId: this.currentFolderId
        });
        this.isVisible = false; // Close modal after selection
    }

    get title() {
        if (this.fileType) {
            return 'Select ' + this.fileType + ' from Google Drive';
        }
        return 'Select File from Google Drive';
    }
}